import {
  Certificate01Icon,
  DataIcon,
  Database01Icon,
  Download02Icon,
  Edit03Icon,
  SlashCircle01Icon,
} from "@/icons";
import { Badge } from "@/kit/ui/badge";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { graphql, useFragment } from "react-relay";
import { Link, generatePath } from "react-router";
import { DatasetCardFragment$key } from "./__generated__/DatasetCardFragment.graphql";
import EntityProfilePic from "./EntityProfilePic";
import { FormattedDataSize } from "./FormattedDataSize";
import { RelativeTime } from "./RelativeTime";
import VoteDisplay from "./VoteDisplay";
import { VDivided, VDividedItem } from "./Divided";
import { splitDatasetSlug } from "@/utils/routing";

const DatasetCardFragment = graphql`
  fragment DatasetCardFragment on Dataset {
    slug
    name
    owner {
      username
      ...EntityProfilePicFragment @arguments(thumbnail: true)
    }
    tags {
      edges {
        node {
          id
          name
        }
      }
    }
    createdAt
    analytics {
      fileSize
      downloadCount
    }
    latestVersion {
      license
    }
    ...VoteDisplayFragment
  }
`;

export interface DatasetCardProps {
  dataset: DatasetCardFragment$key;
}

export default function DatasetCard(props: DatasetCardProps) {
  const dataset = useFragment(DatasetCardFragment, props.dataset);
  const { slug, name, createdAt, owner, tags } = dataset;

  const datasetPath = generatePath(
    "/datasets/:owner/:slug",
    splitDatasetSlug(slug),
  );

  return (
    <div className="group">
      <div className="group flex flex-col w-full h-full rounded-xl border border-grey-200 bg-grey-25 transition-shadow shadow-sm group-hover:shadow-lg group-focus:outline group-focus:outline-1 group-focus:outline-offset-1 group-focus:outline-brand-100">
        <Link
          to={datasetPath}
          className="w-full p-5 rounded-xl flex flex-col gap-4 bg-white shadow-[0_1px_3px_0_rgba(16,24,40,0.10),0_1px_2px_0_rgba(16,24,40,0.06)] flex-1"
        >
          <div className="flex flex-row justify-start items-start gap-3">
            <div className="bg-brand-50 flex items-center justify-center rounded-lg w-8 h-8">
              <Database01Icon className="text-brand-500 w-5 h-5" />
            </div>
            <p className="flex-1 justify-start text-grey-900 text-lg font-semibold leading-7 transition-colors group-hover:text-brand-500 line-clamp-2 overflow-hidden break-all">
              {owner.username} / {name}
            </p>
          </div>

          <div className="min-w-0 overflow-hidden whitespace-nowrap text-ellipsis leading-none">
            {tags.edges.length ? (
              tags.edges.map(({ node }) => (
                <Badge
                  key={node.id}
                  className="inline-flex items-center h-6 px-2 mx-1"
                >
                  <span className="text-sm font-medium leading-tight">
                    {node.name}
                  </span>
                </Badge>
              ))
            ) : (
              <Badge
                hasPrefix
                className="inline-flex items-center h-6 px-2 mx-1 gap-1"
              >
                <SlashCircle01Icon className="w-3 h-3 text-grey-500" />
                <span className="text-sm font-medium leading-tight">
                  <FormattedMessage defaultMessage="No tags yet" />
                </span>
              </Badge>
            )}
          </div>

          <VDivided>
            <VDividedItem
              icon={Edit03Icon}
              title={<FormattedMessage defaultMessage="Updated" />}
            >
              <RelativeTime date={new Date(createdAt)} />
            </VDividedItem>

            <VDividedItem icon={DataIcon}>
              <FormattedDataSize bytes={dataset.analytics.fileSize} />
            </VDividedItem>

            <VDividedItem icon={Download02Icon}>
              <FormattedNumber
                value={dataset.analytics.downloadCount}
                notation="compact"
                maximumFractionDigits={2}
              />
            </VDividedItem>

            {dataset.latestVersion?.license && (
              <VDividedItem icon={Certificate01Icon}>
                {dataset.latestVersion.license}
              </VDividedItem>
            )}
          </VDivided>
        </Link>

        <div className="p-5 flex flex-row items-center justify-between">
          <Link
            to={generatePath("/:username", { username: owner.username })}
            className="flex flex-row items-center gap-3"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <EntityProfilePic entity={owner} size="10" />
            {owner.username}
          </Link>
          <VoteDisplay subject={dataset} kind="compact" to={datasetPath} />
        </div>
      </div>
    </div>
  );
}
